import { patchUserDangerously } from 'api/v2/users/settings';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Toggle } from 'components/atoms/Form/Toggle';
import LoadingSpinner from 'components/atoms/LoadingSpinner/LoadingSpinner.component';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useAccountableBanks from 'hooks/Bank/useAccountableBanks';
import useQueryClient from 'hooks/react-query/useClientQuery';
import useMutation from 'hooks/react-query/useMutation';
import { useEffect, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCacheKeys } from 'types/cacheKeys.types';
import showToast from 'utils/toast';
import type { ActivationStepProps } from 'components/pages/Banks/BankingSignUp/ActivationFlow/types';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';

/**
 * Hook to update user IBAN with proper cache invalidation
 * Defined inline since it's only used in this component
 */
const useUpdateUserIBAN = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (iban: string | null) =>
      patchUserDangerously({ IBAN: iban }, { skipWarningChecks: true }),

    onSuccess: () => {
      queryClient.invalidateQueries([UserCacheKeys.UserData]);
    },

    onError: (error) => {
      console.error('Failed to update IBAN:', error);
      showToast({
        title: t('something_went_wrong'),
        variant: 'error',
      });
    },
  });
};

const useSetAbIbanAsDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (useAbIban: boolean) =>
      patchUserDangerously(
        { useAbIbanAsDefaultWhenReady: useAbIban },
        { skipWarningChecks: true },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([UserCacheKeys.UserData]);
    },
  });
};

// Language-specific invoice images
import invoiceImageDe from './images/invoice_payment_de@3x.webp';
import invoiceImageEn from './images/invoice_payment_en@3x.webp';
import invoiceImageFr from './images/invoice_payment_fr@3x.webp';
import invoiceImageNl from './images/invoice_payment_nl@3x.webp';

const LANGUAGE_IMAGES: Record<string, string> = {
  en: invoiceImageEn,
  de: invoiceImageDe,
  fr: invoiceImageFr,
  nl: invoiceImageNl,
};

const DEBOUNCE_TIME = 200;

interface AccIbanOnInvoicesStepProps extends ActivationStepProps {
  isPreAccount?: boolean;
}

/**
 * Step to enable Accountable IBAN on invoices
 * Full integration with user settings
 * Bookmark step for tracking
 */
const AccIbanOnInvoicesStep: FC<AccIbanOnInvoicesStepProps> = ({
  onContinue,
  onBack,
  isPreAccount,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t, i18n } = useTranslation();
  const { mainAccountableBank } = useAccountableBanks();
  const { data: user } = useGetCurrentCustomerData();
  const { mutateAsync: updateIBANMutationAsync, isLoading: isUpdating } =
    useUpdateUserIBAN();
  const { mutateAsync: setAbIbanDefault, isLoading: isSettingAbIbanDefault } =
    useSetAbIbanAsDefault();

  const invoiceImage = LANGUAGE_IMAGES[i18n.language] || LANGUAGE_IMAGES.en;

  const accountableBankIban = mainAccountableBank?.bankAccountReference?.IBAN;
  const currentUserIban = user?.IBAN;

  // Check if Accountable IBAN is already set (from fresh cache data)
  const isAccountableIbanAlreadySet =
    accountableBankIban && currentUserIban === accountableBankIban;

  const serverAbIbanPref = user?.useAbIbanAsDefaultWhenReady;

  const [isEnabled, setIsEnabled] = useState(
    isPreAccount
      ? serverAbIbanPref ?? true
      : isAccountableIbanAlreadySet || false,
  );
  const [isPendingUpdate, setIsPendingUpdate] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const hasAutoEnabledRef = useRef(false);
  const serverStateRef = useRef(isAccountableIbanAlreadySet || false);

  const handleToggle = (newValue: boolean) => {
    if (isPreAccount) {
      setIsEnabled(newValue);
      return;
    }

    if (!accountableBankIban) return;

    setIsPendingUpdate(true);
    setIsEnabled(newValue);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setIsPendingUpdate(false);
      debounceTimerRef.current = null;

      if (newValue === serverStateRef.current) return;

      const ibanToSet = newValue ? accountableBankIban : null;

      try {
        await updateIBANMutationAsync(ibanToSet);
        serverStateRef.current = newValue;
        showToast({
          title: newValue
            ? t('accountable_banking.activation.acc_iban_on_invoices.enabled')
            : t('accountable_banking.activation.acc_iban_on_invoices.disabled'),
          variant: 'success',
        });
      } catch {
        setIsEnabled(!newValue);
      }
    }, DEBOUNCE_TIME);
  };

  const handleContinue = async () => {
    if (isPreAccount) {
      const alreadyMatches = isEnabled === (serverAbIbanPref ?? false);
      if (!alreadyMatches) {
        try {
          await setAbIbanDefault(isEnabled);
        } catch {
          // Non-blocking — preference can be set later
        }
      }
      onContinue();
      return;
    }
    onContinue();
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-enable toggle on step mount
  // Requirement: Every time user enters this step in POST-ACCOUNT mode,
  // automatically enable IBAN
  // Cache invalidation ensures isAccountableIbanAlreadySet is accurate
  useEffect(() => {
    if (isPreAccount) return;
    if (!accountableBankIban) return;
    if (hasAutoEnabledRef.current) return;

    hasAutoEnabledRef.current = true;
    // Always show toggle as enabled
    setIsEnabled(true);

    // Only send request if IBAN is not already set to accountable bank
    if (isAccountableIbanAlreadySet) {
      // IBAN already correct - update serverStateRef to reflect actual state
      serverStateRef.current = true;
      return;
    }

    // Send the enable request
    const autoEnable = async () => {
      try {
        await updateIBANMutationAsync(accountableBankIban);
        serverStateRef.current = true;
        showToast({
          title: t(
            'accountable_banking.activation.acc_iban_on_invoices.auto_enabled',
          ),
          variant: 'success',
        });
      } catch {
        setIsEnabled(false);
      }
    };

    autoEnable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountableBankIban, isAccountableIbanAlreadySet, isPreAccount]);

  const isBusy = isPreAccount
    ? isSettingAbIbanDefault
    : isUpdating || isUpdatingStep || isPendingUpdate;

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      <div
        className="relative flex justify-center items-center mb-8"
        style={{ minHeight: '250px' }}
      >
        {!imageLoaded && (
          <div className="absolute inset-0 flex justify-center items-center">
            <LoadingSpinner size="lg" color="bank" />
          </div>
        )}
        <img
          src={invoiceImage}
          alt="Image above the toggle to enable or disable Accountable IBAN on invoices"
          className={classNames(
            'transition-opacity duration-300 ease-in-out max-w-full h-auto',
            { 'opacity-0': !imageLoaded },
          )}
          style={{ maxHeight: '250px' }}
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-900">
            {t(
              'accountable_banking.activation.acc_iban_on_invoices.toggle_label',
            )}
          </span>
          <Toggle
            enabled={isEnabled}
            onChange={handleToggle}
            disabled={isPreAccount ? false : isUpdating || !accountableBankIban}
          />
        </div>
      </div>

      <Button
        onClick={handleContinue}
        className="w-full"
        color="bank"
        disabled={isBusy}
      >
        {t('continue')}
      </Button>
    </OnboardingStepWrapper>
  );
};

export default AccIbanOnInvoicesStep;
